/**
 * DateRangePicker — Side-by-side date range input.
 *
 * Two date inputs (start and end) with validation that end date >= start date.
 *
 * Usage:
 * ```tsx
 * <DateRangePicker
 *   startDate={startDate}
 *   endDate={endDate}
 *   onStartDateChange={setStartDate}
 *   onEndDateChange={setEndDate}
 *   startLabel="From Date"
 *   endLabel="To Date"
 *   required={true}
 * />
 * ```
 */

import * as React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { cn } from '../../lib/utils';

// ─── Types ─────────────────────────────────────────────────────────

export interface DateRangePickerProps {
  /** Start date value (ISO format: YYYY-MM-DD) */
  startDate: string | null;
  /** End date value (ISO format: YYYY-MM-DD) */
  endDate: string | null;
  /** Start date change handler */
  onStartDateChange: (date: string | null) => void;
  /** End date change handler */
  onEndDateChange: (date: string | null) => void;
  /** Start date label (default: "Start Date") */
  startLabel?: string;
  /** End date label (default: "End Date") */
  endLabel?: string;
  /** Minimum selectable date */
  minDate?: string;
  /** Maximum selectable date */
  maxDate?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Custom class name for the container */
  className?: string;
}

// ─── Component ──────────────────────────────────────────────────────

export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel = 'Start Date',
  endLabel = 'End Date',
  minDate,
  maxDate,
  required = false,
  error,
  disabled = false,
  className,
}: DateRangePickerProps) {
  // ─── Validation ────────────────────────────────────────────────

  // End date min = start date (can't end before start)
  const endDateMin = startDate || minDate;

  // Check if end date is before start date
  const hasDateError = startDate && endDate && endDate < startDate;

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className={cn('space-y-3', className)}>
      {/* Labels */}
      <div className="flex items-center gap-1">
        <Label className="text-sm font-medium text-[var(--color-foreground)]">
          {startLabel}
          {required && <span className="text-[var(--color-destructive)] ml-1">*</span>}
        </Label>
      </div>

      {/* Date Inputs */}
      <div className="grid grid-cols-2 gap-4">
        {/* Start Date */}
        <div className="space-y-1">
          <Input
            type="date"
            value={startDate || ''}
            onChange={(e) => onStartDateChange(e.target.value || null)}
            min={minDate}
            max={maxDate}
            disabled={disabled}
            className={cn(
              'w-full',
              (error || hasDateError) && 'border-[var(--color-destructive)]',
            )}
            aria-label={startLabel}
          />
        </div>

        {/* End Date */}
        <div className="space-y-1">
          <Label className="text-xs font-medium text-[var(--color-muted-foreground)] block">
            {endLabel}
            {required && <span className="text-[var(--color-destructive)]">*</span>}
          </Label>
          <Input
            type="date"
            value={endDate || ''}
            onChange={(e) => onEndDateChange(e.target.value || null)}
            min={endDateMin}
            max={maxDate}
            disabled={disabled}
            className={cn(
              'w-full',
              (error || hasDateError) && 'border-[var(--color-destructive)]',
            )}
            aria-label={endLabel}
          />
        </div>
      </div>

      {/* Error Messages */}
      {(error || hasDateError) && (
        <p className="text-xs text-[var(--color-destructive)]">
          {hasDateError ? 'End date must be after start date' : error}
        </p>
      )}
    </div>
  );
}
